import { getPollSurvey, savePollSurvey } from '@/service/customPoll'

/** *************************/
/** **** Helper Area ********/
/** *************************/
const localState = {
  getPolls: () => (localStorage.polls ? JSON.parse(localStorage.polls) : {}),
  getPoll: pollId => {
    const polls = localState.getPolls()
    return polls[pollId]
  },

  hasPoll: pollId => !!localState.getPoll(pollId),

  savePoll: (pollId, state) => {
    const polls = localState.getPolls()
    polls[pollId] = state
    localStorage.polls = JSON.stringify(polls)
  },

  cleanPoll: pollId => {
    const polls = localState.getPolls()
    delete polls[pollId]
    localStorage.polls = JSON.stringify(polls)
  },
}

const formatToSave = survey => {
  const getAnswer = item => {
    const answer =
      survey.answers[item.question_id] === undefined
        ? null
        : survey.answers[item.question_id]
    const formatByType = {
      'radio-string': a => {
        return { answer: a ? a.text : null }
      },
      'slide-number': a => {
        return { answer: a }
      },
      paragraph: a => {
        const answer = Object.assign({ text: null, isAnonymous: true }, a)
        return { answer: answer.text, isAnonymous: answer.isAnonymous }
      },
    }

    return formatByType[item.question_type](answer)
  }

  const getFeedback = item => {
    const feedback = survey.feedback[item.question_id] || {
      text: null,
      isAnonymous: true,
    }
    if (!survey.feedback[item.question_id]) return {}

    return {
      feedback: {
        question: item.question_text,
        isAnonymous: feedback.isAnonymous,
        answer: feedback.text,
      },
    }
  }

  const response = survey.pollSurvey.questions.map(q => {
    return Object.assign(
      {
        question_id: q.question_id,
        question_type: q.question_type,
        question_text: q.question_text,
      },
      { ...getAnswer(q) },
      { ...getFeedback(q) }
    )
  })

  return { poll_survey: response }
}

function initialState() {
  return {
    pollId: null,
    alreadySent: false,
    pollSurvey: {
      title: '',
      description: '',
      questions: [],
    },
    page: {
      current: null,
      loading: false,
    },
    currentQuestion: {},
    error: null,
    answers: {},
    feedback: {},
  }
}

const formatQuestion = item => {
  const formatByType = {
    paragraph: a => Object.assign({}, a || { text: null, isAnonymous: true }),
  }

  const generateAnswered = formatByType[item.question_type] || (a => a)
  return {
    id: item.question_id,
    text: item.question_text,
    type: item.question_type,
    answers: item.answers || [],
    answered: generateAnswered(item.answered),
    feedback: item.feedback,
  }
}
/** ******************************/
/** **** END - Helper Area *******/
/** ******************************/

const state = initialState()

const getters = {
  pollSurvey: state => state.pollSurvey,
  error: state => state.error,
  alreadySent: state => state.alreadySent,

  currentQuestion: state => {
    if (!state.currentQuestion) return null

    const questionId = state.currentQuestion.question_id
    const answered = state.answers[questionId] || undefined
    const feedback = {
      text: state.currentQuestion.feedback_question,
      answer: state.feedback[questionId] || { isAnonymous: true },
    }

    return formatQuestion(
      Object.assign({}, state.currentQuestion, { answered, feedback })
    )
  },

  currentAnswered: state => state.answers[state.currentQuestion.question_id],

  surveyPage: state =>
    Object.assign(state.page, {
      total: state.pollSurvey.questions.length,
    }),
}

const mutations = {
  POLL_ID: (state, pollId) => (state.pollId = pollId),
  LOAD_POLL_STATE: (state, pollState) => {
    Object.assign(state, pollState)
  },
  POLL_SURVEY: (state, pollSurvey) => (state.pollSurvey = pollSurvey),
  SET_POLL_ALREADY_SENT: state => (state.alreadySent = true),
  ERROR: (state, err) => (state.error = err),
  FEEDBACK: (state, feedback) =>
    (state.feedback[state.currentQuestion.question_id] = feedback),

  ANSWER: (state, answer) => {
    state.answers[state.currentQuestion.question_id] = answer
    state.answers = Object.assign({}, state.answers)
    return state.answers[state.currentQuestion.question_id]
  },

  CURRENT_PAGE: (state, currentPage) => {
    state.page.current = currentPage
    state.currentQuestion = state.pollSurvey.questions[currentPage - 1]
  },
  SET_LOADING: (state, loading) => (state.page.loading = loading),
}

const actions = {
  loadPollState({ commit, state }, pollId) {
    commit('POLL_ID', pollId)

    if (localState.hasPoll(pollId)) {
      return commit('LOAD_POLL_STATE', localState.getPoll(pollId))
    }

    return actions.getPollSurvey({ commit, state }, pollId).catch(err => {
      if (err.response.status === 404 || err.response.status === 400) {
        commit('ERROR', {
          context: 'custom-poll-survey-not-found-or-archived',
          status: 'warning',
          data: err,
        })
      }
      if (err.response.status === 410) {
        commit('ERROR', {
          context: 'custom-poll-survey-already-sent',
          status: 'warning',
          data: err,
        })
      }
    })
  },

  getPollSurvey({ commit, state }, pollId) {
    const setError = error => {
      commit('ERROR', error)
      throw error
    }

    const setPollSurvey = pollSurvey => {
      if (pollSurvey.data.poll_survey) {
        commit('POLL_SURVEY', pollSurvey.data.poll_survey)
        return localState.savePoll(pollId, state)
      }

      const error = {
        context: 'customPoll',
        type: 'unexpectedError',
        message: 'payload inválido',
        data: pollSurvey,
      }

      throw error
    }

    return getPollSurvey(pollId)
      .then(setPollSurvey)
      .catch(setError)
  },

  start({ commit, state }) {
    commit('CURRENT_PAGE', 1)
    localState.savePoll(state.pollId, state)
  },

  changePage({ commit, state }, pageNumber) {
    if (state.pollSurvey.questions[pageNumber - 1]) {
      commit('CURRENT_PAGE', pageNumber)
      localState.savePoll(state.pollId, state)
    }
  },

  sendSurveyPoll({ commit, state }) {
    commit('SET_LOADING', true)
    return savePollSurvey(state.pollId, formatToSave(state))
      .then(() => {
        commit('SET_LOADING', false)
        commit('SET_POLL_ALREADY_SENT')
        localState.savePoll(state.pollId, state)

        setTimeout(() => {
          localState.cleanPoll(state.pollId)
        }, 3000)
      })
      .catch(err => {
        commit('SET_LOADING', false)
        if (err.response.status === 404 || err.response.status === 400) {
          commit('ERROR', {
            context: 'custom-poll-survey-not-found-or-archived',
            status: 'warning',
            data: err,
          })
        }
        if (err.response.status === 410) {
          commit('ERROR', {
            context: 'custom-poll-survey-already-sent',
            status: 'warning',
            data: err,
          })
        }
      })
  },

  selectAnswer({ commit, state }, answer) {
    commit('ANSWER', answer)
    localState.savePoll(state.pollId, state)
  },

  feedback({ commit, state }, feedback) {
    commit('FEEDBACK', feedback)
    localState.savePoll(state.pollId, state)
  },

  isStarted({ state }) {
    return state.page.current > 0
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
